import {
  Children,
  cloneElement,
  isValidElement,
  useCallback,
  useId,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import { mergeHandlers, mergeRefs, useAnchoredPanel } from "./overlay";

export type HoverPopoverPlacement = "above" | "below";

export type HoverPopoverProps = {
  children: ReactNode;
  content: ReactNode;
  placement?: HoverPopoverPlacement;
  contentClassName?: string;
};

export function HoverPopover({
  children,
  content,
  placement = "above",
  contentClassName,
}: HoverPopoverProps) {
  const tooltipId = useId();
  const triggerRef = useRef<HTMLElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const hide = useCallback(() => setOpen(false), []);
  const show = useCallback(() => setOpen(true), []);

  const { panelStyle } = useAnchoredPanel({
    isOpen: open,
    onClose: hide,
    triggerRef,
    panelRef,
    placement,
    align: "center",
    passive: true,
    closeOnOutsideClick: false,
  });

  const child = Children.only(children);
  if (!isValidElement(child)) {
    return <>{children}</>;
  }

  const element = child as ReactElement<{
    ref?: React.Ref<HTMLElement>;
    onMouseEnter?: (event: React.MouseEvent<HTMLElement>) => void;
    onMouseLeave?: (event: React.MouseEvent<HTMLElement>) => void;
    onFocus?: (event: React.FocusEvent<HTMLElement>) => void;
    onBlur?: (event: React.FocusEvent<HTMLElement>) => void;
    "aria-describedby"?: string;
  }>;

  const describedBy = open
    ? [element.props["aria-describedby"], tooltipId].filter(Boolean).join(" ")
    : element.props["aria-describedby"];

  return (
    <>
      {cloneElement(element, {
        ref: mergeRefs(element.props.ref, triggerRef),
        onMouseEnter: mergeHandlers(element.props.onMouseEnter, show),
        onMouseLeave: mergeHandlers(element.props.onMouseLeave, hide),
        onFocus: mergeHandlers(element.props.onFocus, show),
        onBlur: mergeHandlers(element.props.onBlur, hide),
        "aria-describedby": describedBy || undefined,
      })}
      {open &&
        createPortal(
          <div
            id={tooltipId}
            ref={panelRef}
            role="tooltip"
            style={panelStyle}
            className={cn(
              "pointer-events-none z-[200] w-max max-w-xs animate-fade-in rounded-lg bg-surface-900 px-2.5 py-1.5 text-xs font-medium text-surface-0 shadow-popover",
              contentClassName,
            )}
          >
            {content}
          </div>,
          document.body,
        )}
    </>
  );
}
